// @ts-nocheck
/**
 * School Billing Management Page
 * Epic 86: School Subscription Management
 *
 * Features:
 * - Current subscription details and status
 * - Student and teacher license management
 * - Usage analytics and reporting
 * - Invoice history and downloads
 * - Subscription upgrade/modification
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tile,
  Button,
  InlineNotification,
  Loading,
  Tag,
  Heading,
  Grid,
  Column,
  Modal,
  TextInput,
  ProgressBar,
  Stack,
  Section,
  Link,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@carbon/react';
import { Add, Download, Email, Edit, TrashCan, Renew, Settings, ChartLine } from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface Subscription {
  id: string;
  institution_name: string;
  plan_name: string;
  status: string;
  billing_interval: string;
  price: number;
  currency: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  features: string[];
  student_count: number;
  teacher_count: number;
  student_licenses: number;
}

interface Usage {
  ai_tokens_used: number;
  ai_tokens_limit: number;
  active_users: number;
  period_end: number;
}

interface Invoice {
  invoice_id: string;
  invoice_number: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  currency: string;
  status: string;
  due_date: number;
  paid_at: number;
  invoice_pdf_url: string;
  created_at: number;
}

const SchoolBilling: React.FC = () => {
  const { user, token } = useAuthStore();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [hasSubscription, setHasSubscription] = useState(false);

  // Modal states
  const [isAddLicensesModalOpen, setIsAddLicensesModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [newLicenseCount, setNewLicenseCount] = useState('');
  const [cancelConfirmation, setCancelConfirmation] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [operationInProgress, setOperationInProgress] = useState(false);

  // Check role authorization
  useEffect(() => {
    if (!user || (user.role !== 'school_admin' && user.role !== 'district_admin' && user.role !== 'platform_admin')) {
      setError('Access denied. Requires school_admin or district_admin role.');
      setLoading(false);
    }
  }, [user]);

  // Fetch subscription data
  useEffect(() => {
    if (user && (user.role === 'school_admin' || user.role === 'district_admin' || user.role === 'platform_admin')) {
      fetchSubscriptionData();
    }
  }, [user, token]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/v1/subscriptions/school/current`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.has_subscription) {
        setHasSubscription(true);
        setSubscription(response.data.subscription);
        setUsage(response.data.usage);
        setInvoices(response.data.invoices || []);
      } else {
        setHasSubscription(false);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLicenses = async () => {
    if (!newLicenseCount || parseInt(newLicenseCount) < 1) {
      setError('Please enter a valid license count');
      return;
    }

    try {
      setOperationInProgress(true);
      setError(null);

      await axios.patch(
        `${API_BASE_URL}/v1/subscriptions/school/student-count`,
        { new_student_count: parseInt(newLicenseCount) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIsAddLicensesModalOpen(false);
      setNewLicenseCount('');
      await fetchSubscriptionData();
    } catch (err: any) {
      console.error('Failed to update licenses:', err);
      setError(err.response?.data?.message || 'Failed to update licenses');
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (cancelConfirmation !== subscription?.institution_name) {
      setError('Confirmation does not match institution name');
      return;
    }

    try {
      setOperationInProgress(true);
      setError(null);

      await axios.delete(`${API_BASE_URL}/v1/subscriptions/school`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          immediate: false,
          reason: cancelReason,
          confirmation: cancelConfirmation,
        },
      });

      setIsCancelModalOpen(false);
      setCancelConfirmation('');
      setCancelReason('');
      await fetchSubscriptionData();
    } catch (err: any) {
      console.error('Failed to cancel subscription:', err);
      setError(err.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleDownloadInvoice = (invoice: Invoice) => {
    if (invoice.invoice_pdf_url) {
      window.open(invoice.invoice_pdf_url, '_blank');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (cents: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  const getStatusTagType = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'trialing':
        return 'cyan';
      case 'past_due':
        return 'red';
      case 'canceled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const calculateUsagePercentage = () => {
    if (!usage) return 0;
    return Math.min(100, (usage.ai_tokens_used / usage.ai_tokens_limit) * 100);
  };

  const calculateLicenseUtilization = () => {
    if (!subscription) return 0;
    return Math.min(100, (subscription.student_count / subscription.student_licenses) * 100);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Loading description="Loading subscription data..." withOverlay={false} />
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div style={{ padding: '2rem' }}>
        <InlineNotification kind="error" title="Error" subtitle={error} onCloseButtonClick={() => setError(null)} />
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div style={{ padding: '2rem' }}>
        <Heading style={{ marginBottom: '1rem' }}>School Subscription</Heading>
        <InlineNotification
          kind="info"
          title="No Active Subscription"
          subtitle="Contact sales to set up a school subscription"
        />
        <Button kind="primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/contact-sales')}>
          Contact Sales
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <Grid>
        <Column lg={12}>
          <Heading style={{ marginBottom: '0.5rem' }}>School Billing Management</Heading>
          <p style={{ marginBottom: '2rem', color: 'var(--cds-text-secondary)' }}>
            Manage your institution's subscription, licenses, and billing
          </p>
        </Column>
      </Grid>

      {/* Error notification */}
      {error && (
        <Grid style={{ marginBottom: '1rem' }}>
          <Column lg={12}>
            <InlineNotification kind="error" title="Error" subtitle={error} onCloseButtonClick={() => setError(null)} />
          </Column>
        </Grid>
      )}

      {/* Subscription Overview */}
      <Grid style={{ marginBottom: '2rem' }}>
        <Column lg={8}>
          <Tile style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem',
              }}
            >
              <div>
                <Heading style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
                  {subscription?.institution_name}
                </Heading>
                <p style={{ color: 'var(--cds-text-secondary)' }}>{subscription?.plan_name}</p>
              </div>
              <Tag type={getStatusTagType(subscription?.status || '')}>{subscription?.status?.toUpperCase()}</Tag>
            </div>

            <Grid condensed>
              <Column lg={6}>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem' }}>
                    Student Licenses
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                    {subscription?.student_count} / {subscription?.student_licenses}
                  </p>
                  <ProgressBar
                    value={calculateLicenseUtilization()}
                    label="License utilization"
                    helperText={`${subscription?.student_licenses - subscription?.student_count} licenses available`}
                  />
                </div>
              </Column>
              <Column lg={6}>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem' }}>
                    Active Teachers
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>{subscription?.teacher_count}</p>
                </div>
              </Column>
            </Grid>

            <Stack gap={4} style={{ marginTop: '1.5rem' }}>
              <Button kind="primary" renderIcon={Add} onClick={() => setIsAddLicensesModalOpen(true)}>
                Manage Licenses
              </Button>
              <Button
                kind="secondary"
                renderIcon={Email}
                onClick={() => (window.location.href = 'mailto:sales@sigma08.com')}
              >
                Contact Sales
              </Button>
            </Stack>
          </Tile>
        </Column>

        <Column lg={4}>
          <Tile style={{ padding: '1.5rem', marginBottom: '1rem' }}>
            <Heading style={{ fontSize: '1rem', marginBottom: '1rem' }}>Billing Information</Heading>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Billing Cycle</p>
              <p style={{ fontWeight: 600, textTransform: 'capitalize' }}>{subscription?.billing_interval}</p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Amount</p>
              <p style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                {formatCurrency((subscription?.price || 0) * 100, subscription?.currency)}
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Next Billing Date</p>
              <p style={{ fontWeight: 600 }}>{formatDate(subscription?.current_period_end || 0)}</p>
            </div>
            {subscription?.cancel_at_period_end && (
              <InlineNotification
                kind="warning"
                title="Subscription Canceling"
                subtitle={`Access until ${formatDate(subscription.current_period_end)}`}
                lowContrast
                hideCloseButton
              />
            )}
          </Tile>
        </Column>
      </Grid>

      {/* Usage Analytics */}
      <Grid style={{ marginBottom: '2rem' }}>
        <Column lg={12}>
          <Tile style={{ padding: '1.5rem' }}>
            <Heading style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Usage Analytics</Heading>
            <Grid condensed>
              <Column lg={6}>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginBottom: '0.5rem' }}>
                    AI Token Usage This Period
                  </p>
                  <ProgressBar
                    value={calculateUsagePercentage()}
                    label={`${usage?.ai_tokens_used?.toLocaleString()} / ${usage?.ai_tokens_limit?.toLocaleString()} tokens`}
                  />
                </div>
              </Column>
              <Column lg={6}>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem' }}>
                    Active Users This Period
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>{usage?.active_users}</p>
                </div>
              </Column>
            </Grid>
          </Tile>
        </Column>
      </Grid>

      {/* Invoice History */}
      <Grid style={{ marginBottom: '2rem' }}>
        <Column lg={12}>
          <Tile style={{ padding: '1.5rem' }}>
            <Heading style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Invoice History</Heading>
            <DataTable
              rows={invoices}
              headers={[
                { key: 'invoice_number', header: 'Invoice Number' },
                { key: 'created_at', header: 'Date' },
                { key: 'amount_due_cents', header: 'Amount' },
                { key: 'status', header: 'Status' },
                { key: 'actions', header: 'Actions' },
              ]}
            >
              {({ rows, headers, getHeaderProps, getTableProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--cds-text-secondary)' }}>
                              No invoices yet
                            </p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map(invoice => (
                          <TableRow key={invoice.invoice_id}>
                            <TableCell>{invoice.invoice_number}</TableCell>
                            <TableCell>{formatDate(invoice.created_at)}</TableCell>
                            <TableCell>{formatCurrency(invoice.amount_due_cents, invoice.currency)}</TableCell>
                            <TableCell>
                              <Tag type={invoice.status === 'paid' ? 'green' : 'gray'}>
                                {invoice.status.toUpperCase()}
                              </Tag>
                            </TableCell>
                            <TableCell>
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={Download}
                                onClick={() => handleDownloadInvoice(invoice)}
                                disabled={!invoice.invoice_pdf_url}
                              >
                                Download
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </Tile>
        </Column>
      </Grid>

      {/* Danger Zone */}
      <Grid>
        <Column lg={12}>
          <Tile style={{ padding: '1.5rem', border: '1px solid var(--cds-border-subtle-02)' }}>
            <Heading style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--cds-text-error)' }}>
              Danger Zone
            </Heading>
            <p style={{ marginBottom: '1rem', color: 'var(--cds-text-secondary)' }}>
              Canceling your subscription will affect all students and teachers at your institution.
            </p>
            <Button
              kind="danger"
              renderIcon={TrashCan}
              onClick={() => setIsCancelModalOpen(true)}
              disabled={subscription?.cancel_at_period_end}
            >
              Cancel Subscription
            </Button>
          </Tile>
        </Column>
      </Grid>

      {/* Manage Licenses Modal */}
      <Modal
        open={isAddLicensesModalOpen}
        onRequestClose={() => setIsAddLicensesModalOpen(false)}
        modalHeading="Manage Student Licenses"
        primaryButtonText="Update"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleUpdateLicenses}
        primaryButtonDisabled={operationInProgress}
      >
        <p style={{ marginBottom: '1rem' }}>Current licenses: {subscription?.student_licenses}</p>
        <p style={{ marginBottom: '1rem' }}>Active students: {subscription?.student_count}</p>
        <TextInput
          id="license-count"
          labelText="New license count"
          placeholder="Enter number of licenses"
          value={newLicenseCount}
          onChange={e => setNewLicenseCount(e.target.value)}
          type="number"
          min={subscription?.student_count || 1}
        />
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
          Note: You cannot reduce licenses below your current active student count.
        </p>
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal
        danger
        open={isCancelModalOpen}
        onRequestClose={() => setIsCancelModalOpen(false)}
        modalHeading="Cancel School Subscription"
        primaryButtonText="Cancel Subscription"
        secondaryButtonText="Keep Subscription"
        onRequestSubmit={handleCancelSubscription}
        primaryButtonDisabled={operationInProgress || cancelConfirmation !== subscription?.institution_name}
      >
        <InlineNotification
          kind="error"
          title="Warning"
          subtitle="This will affect all students and teachers"
          lowContrast
          hideCloseButton
          style={{ marginBottom: '1rem' }}
        />
        <p style={{ marginBottom: '1rem' }}>
          Subscription will remain active until {formatDate(subscription?.current_period_end || 0)}
        </p>
        <TextInput
          id="cancel-reason"
          labelText="Reason for cancellation (optional)"
          placeholder="Why are you canceling?"
          value={cancelReason}
          onChange={e => setCancelReason(e.target.value)}
          style={{ marginBottom: '1rem' }}
        />
        <TextInput
          id="cancel-confirmation"
          labelText="Type institution name to confirm"
          placeholder={subscription?.institution_name}
          value={cancelConfirmation}
          onChange={e => setCancelConfirmation(e.target.value)}
          invalid={cancelConfirmation !== '' && cancelConfirmation !== subscription?.institution_name}
          invalidText="Institution name does not match"
        />
      </Modal>
    </div>
  );
};

export default SchoolBilling;
